// frontend/src/components/Payroll/ReviewFlagsModal.js
/**
 * Step 3 review-flag triage modal.
 *
 * HR sees every outstanding review flag grouped by type, with a plain-English
 * explanation of what each group means and what to check, before
 * acknowledging — per group or all at once. Replaces the blind
 * "mark 81 items reviewed?" confirmation.
 *
 * Props:
 *   isOpen          - boolean
 *   flags           - flagSummary.needs_review array
 *                     ({id, flag_type, message, employee_payroll__employee__*})
 *   onAcknowledge   - (flagIds: string[]) => void
 *   isAcknowledging - boolean, disables action buttons while the mutation runs
 *   onAutoVerify    - () => void, system cross-check of verifiable flag types
 *   isAutoVerifying - boolean
 *   onClose         - dismiss callback
 */

import React, { useMemo, useState } from 'react';
import {
  AlertTriangle,
  Calculator,
  CalendarClock,
  CheckCircle,
  ChevronDown,
  ChevronRight,
  DollarSign,
  Edit2,
  Info,
  LogOut,
  ShieldCheck,
  TrendingUp,
  UserPlus,
} from 'lucide-react';
import Modal from '../UI/Modal';
import { CountBadge } from '../UI/CountBadge';

// Judgment-heavy groups first; pure-noise calculation warnings last.
const FLAG_TYPE_META = {
  UNPAID_LEAVE: {
    order: 1,
    icon: DollarSign,
    label: 'Unpaid Leave Deductions',
    hint: 'Pay is reduced for these employees. Confirm each deduction matches their approved unpaid leave.',
  },
  MANUAL_OVERRIDE: {
    order: 2,
    icon: Edit2,
    label: 'Manual Adjustments',
    hint: 'Hand-entered adjustments on this run. Confirm the amounts and reasons are intended.',
  },
  NS_DEDUCTION: {
    order: 3,
    icon: AlertTriangle,
    label: 'NS / In-Camp Training',
    hint: 'National Service duty affected pay this month. Verify the ICT day count.',
  },
  MID_MONTH_EXIT: {
    order: 4,
    icon: LogOut,
    label: 'Leavers / Notice Period',
    hint: 'Final pay in progress. Verify last-day pro-ration and outstanding clearances.',
  },
  SALARY_CHANGE: {
    order: 5,
    icon: TrendingUp,
    label: 'Salary Changes',
    hint: 'Salary record changed during the month. Verify the pro-ration split.',
  },
  MID_MONTH_JOIN: {
    order: 6,
    icon: CalendarClock,
    label: 'Mid-Month Joiners',
    hint: 'Salary pro-rated from the start date. Check the day count looks right.',
  },
  FIRST_PAYROLL: {
    order: 7,
    icon: UserPlus,
    label: 'First Payroll',
    hint: 'First run for these employees. Verify salary, bank details and start date.',
  },
  PR_YEAR_1: {
    order: 8,
    icon: Info,
    label: 'New PR — Graduated CPF',
    hint: 'Recently obtained PR status. Graduated CPF contribution rates apply.',
  },
  CALC_WARNING: {
    order: 9,
    icon: Calculator,
    label: 'Calculation Warnings',
    hint: 'Non-blocking notices from the calculator (e.g. a missing levy rate config). Employee pay is not affected.',
  },
};

const fallbackMeta = (flagType) => ({
  order: 99,
  icon: Info,
  label: (flagType || 'Other')
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase()),
  hint: 'Review the message on each item.',
});

const employeeLabel = (flag) => {
  const first = flag.employee_payroll__employee__first_name || '';
  const last = flag.employee_payroll__employee__last_name || '';
  const staffId = flag.employee_payroll__employee__staff_id || '';
  const name = `${first} ${last}`.trim();
  return name ? `${name} (${staffId})` : staffId;
};

const ReviewFlagsModal = ({
  isOpen,
  flags,
  onAcknowledge,
  isAcknowledging,
  onAutoVerify,
  isAutoVerifying,
  onClose,
}) => {
  const [expandedTypes, setExpandedTypes] = useState(() => new Set());

  const groups = useMemo(() => {
    const byType = {};
    (flags || []).forEach((flag) => {
      (byType[flag.flag_type] = byType[flag.flag_type] || []).push(flag);
    });
    return Object.entries(byType)
      .map(([flagType, items]) => ({
        flagType,
        items,
        meta: FLAG_TYPE_META[flagType] || fallbackMeta(flagType),
      }))
      .sort((a, b) => a.meta.order - b.meta.order);
  }, [flags]);

  const totalCount = flags?.length || 0;

  const toggleExpanded = (flagType) => {
    setExpandedTypes((prev) => {
      const next = new Set(prev);
      if (next.has(flagType)) next.delete(flagType);
      else next.add(flagType);
      return next;
    });
  };

  const footer = (
    <>
      <button
        type="button"
        onClick={onClose}
        className="btn btn-admin-outline"
        disabled={isAcknowledging}
      >
        Close
      </button>
      <button
        type="button"
        onClick={() => onAcknowledge((flags || []).map((f) => f.id))}
        className="btn btn-admin-primary"
        disabled={isAcknowledging || totalCount === 0}
      >
        {isAcknowledging ? (
          <>
            <span className="loading-spinner-sm mr-2" />
            Acknowledging...
          </>
        ) : (
          <>
            <CheckCircle className="w-4 h-4 mr-2" />
            <span>Acknowledge All</span>
            <CountBadge count={totalCount} theme="admin" active max={9999} className="ml-2" />
          </>
        )}
      </button>
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Review Outstanding Flags"
      size="lg"
      footer={footer}
      theme="admin"
    >
      <p className="text-sm text-gray-600 mb-4">
        The payroll calculator raised {totalCount} informational flag(s) that need an HR
        sign-off before approval. Expand a group to see who is affected, then acknowledge
        it — or acknowledge everything once you are satisfied. Blocking errors are handled
        separately and are not listed here.
      </p>

      {totalCount > 0 && onAutoVerify && (
        <div className="flex items-center gap-3 p-3 mb-4 bg-blue-50 border border-blue-200 rounded-lg">
          <ShieldCheck className="w-5 h-5 text-blue-600 flex-shrink-0" />
          <p className="text-xs text-blue-800 flex-1">
            The system can cross-check most of these against its own records — attendance
            and leave sessions (Step 2), salary records, and payment setup — and resolve the
            ones that corroborate. Only items needing human judgment will remain.
          </p>
          <button
            type="button"
            onClick={onAutoVerify}
            disabled={isAutoVerifying || isAcknowledging}
            className="btn btn-sm btn-admin-primary flex-shrink-0"
          >
            {isAutoVerifying ? (
              <>
                <span className="loading-spinner-sm mr-2" />
                Verifying...
              </>
            ) : (
              'Auto-verify'
            )}
          </button>
        </div>
      )}

      {totalCount === 0 && (
        <div className="flex items-center gap-2 p-4 bg-green-50 border border-green-200 rounded-lg">
          <CheckCircle className="w-5 h-5 text-green-600" />
          <p className="text-sm text-green-800">All review flags have been acknowledged.</p>
        </div>
      )}

      <div className="space-y-3">
        {groups.map(({ flagType, items, meta }) => {
          const Icon = meta.icon;
          const isExpanded = expandedTypes.has(flagType);
          return (
            <div key={flagType} className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="flex items-center gap-3 px-4 py-3 bg-gray-50">
                <button
                  type="button"
                  onClick={() => toggleExpanded(flagType)}
                  className="flex items-center gap-3 flex-1 min-w-0 text-left"
                  aria-expanded={isExpanded}
                >
                  {isExpanded
                    ? <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    : <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />}
                  <Icon className="w-4 h-4 text-gray-500 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-900">{meta.label}</span>
                      <CountBadge count={items.length} theme="admin" max={9999} />
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">{meta.hint}</p>
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => onAcknowledge(items.map((f) => f.id))}
                  disabled={isAcknowledging}
                  className="btn btn-sm btn-admin-outline flex-shrink-0"
                  title={`Mark all ${items.length} ${meta.label} flag(s) as reviewed`}
                >
                  Acknowledge
                </button>
              </div>

              {isExpanded && (
                <ul className="divide-y divide-gray-100 max-h-64 overflow-y-auto">
                  {items.map((flag) => (
                    <li key={flag.id} className="px-4 py-2.5 flex items-start gap-3">
                      <span className="text-xs font-medium text-gray-900 whitespace-nowrap mt-0.5">
                        {employeeLabel(flag)}
                      </span>
                      <span className="text-xs text-gray-600 break-words">{flag.message}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </div>
    </Modal>
  );
};

export default ReviewFlagsModal;
